/**
 * One node, drawn as SVG.
 *
 * Memoised on identity: the graph compiler hands out stable node ids and
 * structurally-shared node objects, so a keystroke that does not affect this
 * node skips its render entirely. That is what keeps a several-hundred-node
 * diagram responsive while the user types.
 */
import { memo } from 'react';
import type { GraphNode } from '../graph/model/types';
import { shapeGeometry } from './shapes';
import { applyColorOverride, colorsForRole } from './palette';
import { iconPaths, NODE_ICON_VIEWBOX } from './nodeIcons';

export interface DiagramNodeProps {
  readonly node: GraphNode;
  readonly selected: boolean;
  readonly hovered: boolean;
  /** Dimmed because something else is highlighted. */
  readonly muted: boolean;
  readonly fontSize: number;
  readonly lineHeight: number;
  readonly onSelect: (id: string, event: React.MouseEvent<SVGGElement>) => void;
  readonly onActivate: (id: string) => void;
  readonly onHover: (id: string | null) => void;
}

function DiagramNodeImpl({
  node,
  selected,
  hovered,
  muted,
  fontSize,
  lineHeight,
  onSelect,
  onActivate,
  onHover,
}: DiagramNodeProps) {
  const geometry = shapeGeometry(node.shape, node.size);
  const colors = applyColorOverride(colorsForRole(node.role), node.metadata.color);
  const icons = iconPaths(node.metadata.icon);
  const hasIcon = icons.length > 0;

  if (node.shape === 'junction') {
    return (
      <g transform={`translate(${node.position.x} ${node.position.y})`} aria-hidden="true">
        <path d={geometry.outline} fill="var(--fs-edge)" opacity={0.55} />
      </g>
    );
  }

  const { textBox } = geometry;
  const iconSize = 18;
  const iconGap = hasIcon ? iconSize + 6 : 0;
  const textLeft = textBox.x + iconGap;
  const textWidth = Math.max(0, textBox.width - iconGap);
  const centreX = textLeft + textWidth / 2;
  const blockHeight = node.lines.length * lineHeight;
  const firstBaseline = textBox.y + (textBox.height - blockHeight) / 2 + fontSize * 0.82;

  const strokeWidth = selected ? 2.5 : hovered ? 2 : 1.5;
  const outlineStroke = selected
    ? 'var(--fs-selection)'
    : hovered
      ? 'var(--fs-hover)'
      : colors.stroke;

  return (
    <g
      transform={`translate(${node.position.x} ${node.position.y})`}
      className={
        node.metadata.className
          ? node.metadata.className
              .split(' ')
              .map((token) => `fs-user-${token}`)
              .join(' ')
          : undefined
      }
      data-node-id={node.id}
      data-role={node.role}
      opacity={muted ? 0.32 : 1}
      style={{ cursor: 'pointer' }}
      onPointerDown={(event) => {
        // Claim the click before the canvas starts a pan gesture.
        event.stopPropagation();
      }}
      onClick={(event) => onSelect(node.id, event)}
      onDoubleClick={() => onActivate(node.id)}
      onPointerEnter={() => onHover(node.id)}
      onPointerLeave={() => onHover(null)}
    >
      {selected ? (
        <path
          d={geometry.outline}
          fill="none"
          stroke="var(--fs-selection)"
          strokeWidth={7}
          strokeLinejoin="round"
          opacity={0.22}
        />
      ) : null}

      <path
        d={geometry.outline}
        fill={colors.fill}
        stroke={outlineStroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeDasharray={node.role === 'error' ? '5 4' : undefined}
      />

      {geometry.details.map((detail, index) => (
        <path
          key={index}
          d={detail}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={1.25}
          opacity={0.8}
        />
      ))}

      {hasIcon ? (
        <g
          transform={`translate(${textBox.x} ${textBox.y + textBox.height / 2 - iconSize / 2}) scale(${iconSize / NODE_ICON_VIEWBOX})`}
          fill="none"
          stroke={colors.text}
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.85}
          aria-hidden="true"
        >
          {icons.map((d, index) => (
            <path key={index} d={d} />
          ))}
        </g>
      ) : null}

      <text
        x={centreX}
        y={firstBaseline}
        textAnchor="middle"
        fill={colors.text}
        fontSize={fontSize}
        fontWeight={node.role === 'start' || node.role === 'end' ? 600 : 450}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {node.lines.map((line, index) => (
          <tspan key={index} x={centreX} dy={index === 0 ? 0 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>

      {node.metadata.note ? (
        <title>{`${node.label} — ${node.metadata.note}`}</title>
      ) : (
        <title>{node.label}</title>
      )}
    </g>
  );
}

export const DiagramNode = memo(DiagramNodeImpl);
